import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { getEducatorInvitationsRoute, getEducatorRouteBase, getLearnerNavLabel, isEducatorRole, isParentRole, EDUCATOR_SUITE_ENABLED } from '@/lib/educators';
import { EducatorSuiteSoftNavButton } from '@/components/educator/EducatorSuiteComingSoon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Menu, X, ChevronRight, Settings } from 'lucide-react';
import { BrandLogo } from '@/components/BrandMark';
import NotificationBell from '@/components/NotificationBell';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => {
    if (location.pathname === path) return true;
    if (path === '/tutor' || path === '/parent') return false;
    const basePaths = ['/practice', '/theory', '/analytics'];
    return basePaths.includes(path) && location.pathname.startsWith(`${path}/`);
  };

  const educatorBaseRoute = getEducatorRouteBase(user?.role);
  const educatorInvitationsRoute = getEducatorInvitationsRoute(user?.role);
  const isParent = isParentRole(user?.role);
  const learnerNavLabel = getLearnerNavLabel(user?.role);

  const NavLink = ({ to, children, testId, variant = 'default' }) => {
    const isActiveLink = isActive(to);

    if (variant === 'button') {
      return (
        <Link
          to={to}
          className={`nav-button text-xs 2xl:text-sm font-semibold px-3 2xl:px-5 py-2.5 rounded-full transition-all duration-300 whitespace-nowrap ${
            isActiveLink
              ? 'nav-button-active'
              : 'nav-button-inactive'
          }`}
          data-testid={testId}
        >
          {children}
        </Link>
      );
    }

    const baseClasses = "text-sm font-medium transition-all duration-300 ease-out relative group px-1";
    const activeClasses = isActiveLink
      ? 'text-primary'
      : 'text-foreground/70 hover:text-foreground';

    return (
      <Link
        to={to}
        className={`${baseClasses} ${activeClasses}`}
        data-testid={testId}
      >
        {children}
        <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 h-0.5 bg-primary rounded-full transition-all duration-300 ease-out ${
          isActiveLink ? 'w-3/4' : 'w-0 group-hover:w-3/4'
        }`} />
      </Link>
    );
  };

  return (
    <nav
      className={`sticky z-50 w-full top-0 pt-safe bg-background/85 backdrop-blur-md border-b transition-[border-color] duration-3 ease-out ${
        scrolled ? 'border-border' : 'border-transparent'
      }`}
    >
      <div className="max-w-[90rem] mx-auto w-full">
        <div className="flex h-14 md:h-16 items-center justify-between gap-2 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link
            to="/"
            className="navbar-logo flex items-center shrink-0"
            data-testid="navbar-logo"
          >
            <BrandLogo markSize={28} textClassName="text-lg sm:text-xl" />
          </Link>

          {/* Desktop Navigation — «капсула» по центру */}
          <div
            className="hidden xl:flex flex-1 justify-center min-w-0 px-2"
            data-testid="desktop-nav-shell"
          >
            <div className="inline-flex items-center gap-0.5 2xl:gap-1 rounded-full px-1 py-1">
            {user ? (
              <>
                {user.role === 'student' ? (
                  <>
                    <NavLink to="/dashboard" testId="nav-dashboard-link" variant="button">

                      Кабинет
                    </NavLink>
                    <NavLink to="/theory" testId="nav-theory-link" variant="button">

                      Репетитор
                    </NavLink>
                    <NavLink to="/practice" testId="nav-practice-link" variant="button">

                      Практика
                    </NavLink>
                    <NavLink to="/analytics" testId="nav-analytics-link" variant="button">

                      Аналитика
                    </NavLink>
                    <NavLink to="/pricing" testId="nav-pricing-link" variant="button">

                      Подписка
                    </NavLink>
                  </>
                ) : isParent ? (
                  <>
                    <NavLink to="/parent" testId="nav-parent-link" variant="button">

                      Кабинет
                    </NavLink>
                    <NavLink to="/parent/children" testId="nav-parent-children-link" variant="button">
                      {learnerNavLabel}
                    </NavLink>
                    <NavLink to="/parent/progress" testId="nav-parent-progress-link" variant="button">

                      Прогресс
                    </NavLink>
                    <NavLink to="/parent/pricing" testId="nav-parent-pricing-link" variant="button">

                      Тариф
                    </NavLink>
                  </>
                ) : isEducatorRole(user.role) ? (
                  <>
                    <NavLink to={educatorBaseRoute} testId="nav-tutor-link" variant="button">

                      Кабинет
                    </NavLink>
                    <NavLink to={`${educatorBaseRoute}/crm`} testId="nav-tutor-crm-link" variant="button">
                      CRM
                    </NavLink>
                    <NavLink to={`${educatorBaseRoute}/students`} testId="nav-tutor-students-link" variant="button">
                      {learnerNavLabel}
                    </NavLink>
                    <NavLink to={`${educatorBaseRoute}/analytics`} testId="nav-tutor-analytics-link" variant="button">

                      Аналитика
                    </NavLink>
                    <NavLink to={`${educatorBaseRoute}/invitations`} testId="nav-tutor-invitations-link" variant="button">

                      Приглашения
                    </NavLink>
                    <NavLink to={`${educatorBaseRoute}/pricing`} testId="nav-tutor-pricing-link" variant="button">

                      Тариф
                    </NavLink>
                  </>
                ) : user.role === 'admin' ? (
                  <>
                    <NavLink to="/admin" testId="nav-admin-link" variant="button">
                      Админка
                    </NavLink>
                    <NavLink to="/settings" testId="nav-settings-link" variant="button">
                      Настройки
                    </NavLink>
                  </>
                ) : null}
              </>
            ) : (
              <>
                <NavLink to="/#how-it-works" testId="nav-how-it-works-link" variant="button">
                  Как это работает
                </NavLink>
                {EDUCATOR_SUITE_ENABLED ? (
                  <>
                    <NavLink to="/for-tutors" testId="nav-tutors-link" variant="button">
                      Репетиторам
                    </NavLink>
                    <NavLink to="/for-parents" testId="nav-parents-link" variant="button">
                      Родителям
                    </NavLink>
                  </>
                ) : (
                  <>
                    <EducatorSuiteSoftNavButton
                      testId="nav-tutors-link"
                      dialogVariant="nav_marketing"
                      className="nav-button text-xs 2xl:text-sm font-semibold px-3 2xl:px-5 py-2.5 rounded-full transition-all duration-300 whitespace-nowrap nav-button-inactive"
                    >
                      Репетиторам
                    </EducatorSuiteSoftNavButton>
                    <EducatorSuiteSoftNavButton
                      testId="nav-parents-link"
                      dialogVariant="nav_marketing"
                      className="nav-button text-xs 2xl:text-sm font-semibold px-3 2xl:px-5 py-2.5 rounded-full transition-all duration-300 whitespace-nowrap nav-button-inactive"
                    >
                      Родителям
                    </EducatorSuiteSoftNavButton>
                  </>
                )}
                <NavLink to="/pricing" testId="nav-pricing-link" variant="button">

                  Подписка
                </NavLink>
              </>
            )}
            </div>
          </div>

          {/* Right side */}
          <div className="hidden xl:flex items-center gap-1.5 2xl:gap-2 shrink-0">
            {user ? (
              <>
                {(user.role === 'student' || isEducatorRole(user.role)) && (
                  <NotificationBell />
                )}
                {user.role === 'student' && (
                  <Link to="/settings" data-testid="nav-settings-link">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full px-3 py-2 text-foreground/70 transition-all duration-300"
                    >
                      <Settings size={18} className="md:mr-1.5" />
                      <span className="hidden lg:inline">Настройки</span>
                    </Button>
                  </Link>
                )}
                {(isEducatorRole(user.role) || user.role === 'admin') && (
                  <Link to="/settings" data-testid="nav-settings-link">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full px-3 py-2"
                    >
                      <Settings size={18} className="md:mr-1.5" />
                      <span className="hidden lg:inline">Настройки</span>
                    </Button>
                  </Link>
                )}
                <div className="w-px h-6 bg-border/50 mx-0.5" />
                <Button
                  onClick={() => setShowLogoutDialog(true)}
                  variant="ghost"
                  size="sm"
                  className="rounded-full px-3 py-2 font-medium transition-all duration-300"
                  data-testid="logout-button"
                >
                  Выйти
                </Button>
              </>
            ) : (
              <>
                <Link to="/register" data-testid="nav-register-link">
                  <Button size="sm" className="pl-5 pr-4 gap-1.5">
                    Начать
                    <ChevronRight size={16} />
                  </Button>
                </Link>
                <Link to="/login" data-testid="nav-login-link">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full px-5 py-2 font-medium transition-all duration-300"
                  >
                    Войти
                  </Button>
                </Link>
              </>
            )}
            <div className="w-px h-6 bg-border/50 mx-0.5" />
            <ThemeToggle />
          </div>

          {/* Mobile menu buttons */}
          <div className="xl:hidden flex items-center gap-2">
            <ThemeToggle testId="theme-toggle-mobile" />
            <Button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              variant="ghost"
              size="icon"
              className="rounded-full h-11 w-11"
              data-testid="mobile-menu-button"
              aria-label={mobileMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav-menu"
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </Button>
          </div>
        </div>

      {/* Mobile Menu */}
      <div
        id="mobile-nav-menu"
        className={`xl:hidden transition-all duration-300 ease-out ${
        mobileMenuOpen ? 'max-h-[min(80vh,28rem)] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0 overflow-hidden'
      }`}
      >
        <div className="px-4 sm:px-6 py-4 space-y-3 border-t border-border bg-background/95 backdrop-blur-md pb-[max(1.25rem,env(safe-area-inset-bottom,0px))]">
          {user?.role === 'student' && (
            <div className="flex items-center justify-between px-1">
              <span className="text-sm font-medium text-muted-foreground">Уведомления</span>
              <NotificationBell />
            </div>
          )}
          {isEducatorRole(user?.role) && (
            <div className="flex items-center justify-between px-1">
              <span className="text-sm font-medium text-muted-foreground">Уведомления</span>
              <NotificationBell />
            </div>
          )}
          {user ? (
            <>
              {user.role === 'student' ? (
                <>
                  {[
                    { to: '/dashboard', label: 'Кабинет' },
                    { to: '/theory', label: 'Репетитор' },
                    { to: '/practice', label: 'Практика' },
                    { to: '/analytics', label: 'Аналитика' },
                    { to: '/pricing', label: 'Подписка' },
                    { to: '/settings', label: 'Настройки' },
                  ].map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`block py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive(link.to)
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'hover:bg-foreground/[0.05] hover:text-foreground'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </>
              ) : isParent ? (
                <>
                  {[
                    { to: '/parent', label: 'Кабинет' },
                    { to: '/parent/children', label: learnerNavLabel },
                    { to: '/parent/progress', label: 'Прогресс' },
                    { to: '/parent/notifications', label: 'Уведомления' },
                    { to: '/parent/pricing', label: 'Тариф' },
                    { to: '/settings', label: 'Настройки' },
                  ].map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`block py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive(link.to)
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'hover:bg-foreground/[0.05] hover:text-foreground'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </>
              ) : isEducatorRole(user.role) ? (
                <>
                    {[
                    { to: educatorBaseRoute, label: 'Кабинет' },
                    { to: `${educatorBaseRoute}/crm`, label: 'CRM' },
                    { to: `${educatorBaseRoute}/students`, label: learnerNavLabel },
                    { to: `${educatorBaseRoute}/homework`, label: 'Домашка' },
                    { to: `${educatorBaseRoute}/analytics`, label: 'Аналитика' },
                    { to: `${educatorBaseRoute}/invitations`, label: 'Приглашения' },
                    { to: `${educatorBaseRoute}/pricing`, label: 'Тариф' },
                    { to: '/settings', label: 'Настройки' },
                  ].map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`block py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive(link.to)
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'hover:bg-foreground/[0.05] hover:text-foreground'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </>
              ) : user.role === 'admin' ? (
                <>
                  {[
                    { to: '/admin', label: 'Админка' },
                    { to: '/settings', label: 'Настройки' },
                  ].map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`block py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive(link.to)
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'hover:bg-foreground/[0.05] hover:text-foreground'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </>
              ) : null}
              <div className="pt-3 pb-1">
                <Button
                  onClick={() => {
                    setShowLogoutDialog(true);
                    setMobileMenuOpen(false);
                  }}
                  variant="outline"
                  className="w-full rounded-xl"
                >
                  Выйти
                </Button>
              </div>
            </>
          ) : (
            <>
              {EDUCATOR_SUITE_ENABLED ? (
                [
                  { to: '/#how-it-works', label: 'Как это работает' },
                  { to: '/for-tutors', label: 'Репетиторам' },
                  { to: '/for-parents', label: 'Родителям' },
                  { to: '/pricing', label: 'Подписка' },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`block py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive(link.to)
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'hover:bg-foreground/[0.05] hover:text-foreground'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))
              ) : (
                <>
                  {[
                    { to: '/#how-it-works', label: 'Как это работает' },
                    { to: '/pricing', label: 'Подписка' },
                  ].map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`block py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive(link.to)
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'hover:bg-foreground/[0.05] hover:text-foreground'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <EducatorSuiteSoftNavButton
                    dialogVariant="nav_marketing"
                    onBeforeOpen={() => setMobileMenuOpen(false)}
                    className={`block w-full py-3 px-4 rounded-xl text-sm font-medium text-left transition-all duration-200 ${
                      isActive('/for-tutors')
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'hover:bg-foreground/[0.05] hover:text-foreground'
                    }`}
                  >
                    Репетиторам
                  </EducatorSuiteSoftNavButton>
                  <EducatorSuiteSoftNavButton
                    dialogVariant="nav_marketing"
                    onBeforeOpen={() => setMobileMenuOpen(false)}
                    className={`block w-full py-3 px-4 rounded-xl text-sm font-medium text-left transition-all duration-200 ${
                      isActive('/for-parents')
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'hover:bg-foreground/[0.05] hover:text-foreground'
                    }`}
                  >
                    Родителям
                  </EducatorSuiteSoftNavButton>
                </>
              )}
              <div className="flex gap-3 pt-3 pb-1">
                <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                  <Button className="w-full rounded-xl font-semibold ">Начать</Button>
                </Link>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                  <Button variant="outline" className="w-full rounded-xl font-medium">
                    Войти
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-semibold tracking-tight">
              Выйти из аккаунта?
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Несохранённые данные будут потеряны.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-3 pt-6 sm:flex-row sm:gap-4">
            <Button
              variant="outline"
              onClick={() => setShowLogoutDialog(false)}
              className="rounded-full h-11"
            >
              Нет, остаюсь
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                logout();
                setShowLogoutDialog(false);
              }}
              className="rounded-full h-11"
            >
              Да, выйти
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </nav>
  );
};
